import { useEffect, useMemo, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { DatePicker } from '@/components/ui/date-picker';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { Navigate } from 'react-router-dom';
import { useAuthStore } from '@/lib/auth/store';
import { BASE_PERMISSION } from '@/lib/constants/modules';
import { usePermission } from '@/lib/permissions/use-permission';
import { STATUS_CODE_TO_LABEL } from '@/lib/constants/status';
import { TEMPLATES } from '@/lib/constants/templates';
import { formatDate } from '@/lib/utils/date';
import { useUserDetails } from '@/features/users/api';
import { useResellerSubUsers, useResellerSummary } from '../api';
import type { ResellerSummaryRow } from '../types';
import { dashIfEmpty } from '../utils';

const ANY = '__any__';

export function ResellerSummaryPage() {
  const userId = useAuthStore((s) => s.userId);
  const canView = usePermission('Manage Users.Reseller Summary', BASE_PERMISSION.VIEW);
  const profile = useUserDetails(userId);
  const parentType = profile.data?.parentType ?? '';
  const isReseller = parentType === 'reseller';

  const subUsers = useResellerSubUsers();
  const summary = useResellerSummary();
  const rows = summary.data ?? [];

  const [username, setUsername] = useState('');
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);

  const runQuery = (overrides?: Partial<{ username: string }>) => {
    if (!userId) return;
    if (startDate && endDate && startDate > endDate) {
      toast.error('Start date cannot be after end date');
      return;
    }
    summary.mutate({
      userId: String(userId),
      username: overrides?.username ?? username,
      startDate: startDate ? formatDate(startDate) : '',
      endDate: endDate ? formatDate(endDate) : '',
    });
  };

  /* Initial unfiltered load. */
  useEffect(() => {
    if (!userId) return;
    runQuery();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const columns = useMemo<ColumnDef<ResellerSummaryRow>[]>(() => {
    const r1: ColumnDef<ResellerSummaryRow>[] = [
      {
        accessorKey: 'reseller1Username',
        header: 'Reseller 1',
        cell: ({ row }) => dashIfEmpty(row.original.reseller1Username),
      },
      {
        accessorKey: 'reseller1PlanName',
        header: 'R1 plan',
        cell: ({ row }) => dashIfEmpty(row.original.reseller1PlanName),
      },
      {
        accessorKey: 'reseller1Pulses',
        header: 'R1 pulse',
        size: 110,
        cell: ({ row }) => (
          <span className="tabular-nums">{dashIfEmpty(row.original.reseller1Pulses)}</span>
        ),
      },
    ];
    const r2: ColumnDef<ResellerSummaryRow>[] = [
      {
        accessorKey: 'reseller2Username',
        header: 'Reseller 2',
        cell: ({ row }) => dashIfEmpty(row.original.reseller2Username),
      },
      {
        accessorKey: 'reseller2PlanName',
        header: 'R2 plan',
        cell: ({ row }) => dashIfEmpty(row.original.reseller2PlanName),
      },
    ];
    const common: ColumnDef<ResellerSummaryRow>[] = [
      {
        accessorKey: 'username',
        header: 'User',
        cell: ({ row }) => dashIfEmpty(row.original.username),
      },
      {
        accessorKey: 'templateId',
        header: 'Campaign type',
        size: 150,
        cell: ({ row }) => {
          const id = row.original.templateId;
          const label = id !== undefined ? TEMPLATES[Number(id)] : undefined;
          return <Badge variant="outline">{label ?? dashIfEmpty(id)}</Badge>;
        },
      },
      {
        accessorKey: 'userPulseDuration',
        header: 'Pulse dur.',
        size: 110,
        cell: ({ row }) => (
          <span className="tabular-nums">{dashIfEmpty(row.original.userPulseDuration)}</span>
        ),
      },
      {
        accessorKey: 'userPulsePrice',
        header: 'Pulse price',
        size: 120,
        cell: ({ row }) => (
          <span className="tabular-nums">
            {row.original.userPulsePrice ? `₹${row.original.userPulsePrice}` : '—'}
          </span>
        ),
      },
      {
        accessorKey: 'campaignName',
        header: 'Campaign',
        cell: ({ row }) => dashIfEmpty(row.original.campaignName),
      },
      {
        accessorKey: 'scheduleDate',
        header: 'Started',
        size: 170,
        cell: ({ row }) => dashIfEmpty(row.original.scheduleDate),
      },
      {
        accessorKey: 'endDate',
        header: 'Ended',
        size: 170,
        cell: ({ row }) => dashIfEmpty(row.original.endDate),
      },
      {
        accessorKey: 'campaignStatus',
        header: 'Status',
        size: 130,
        cell: ({ row }) => {
          const code = row.original.campaignStatus;
          const label = code !== undefined ? STATUS_CODE_TO_LABEL[Number(code)] : undefined;
          return <Badge variant="outline">{label ?? dashIfEmpty(code)}</Badge>;
        },
      },
      {
        id: 'tcd',
        header: 'TCD',
        size: 100,
        cell: ({ row }) => {
          const dialed = Number(row.original.callsDailed ?? 0) || 0;
          const retries = row.original.retryCount ?? 0;
          return (
            <span
              className="tabular-nums"
              title="Total calls dialed = dialed + retries"
            >
              {(dialed + retries).toLocaleString()}
            </span>
          );
        },
      },
      {
        accessorKey: 'callsDailed',
        header: 'Dialed',
        size: 100,
        cell: ({ row }) => (
          <span className="tabular-nums">{dashIfEmpty(row.original.callsDailed)}</span>
        ),
      },
      {
        accessorKey: 'retryCount',
        header: 'Retries',
        size: 90,
        cell: ({ row }) => (
          <span className="tabular-nums">{row.original.retryCount ?? 0}</span>
        ),
      },
      {
        accessorKey: 'callsConnected',
        header: 'Connected',
        size: 110,
        cell: ({ row }) => (
          <span className="tabular-nums">{dashIfEmpty(row.original.callsConnected)}</span>
        ),
      },
      {
        accessorKey: 'reseller2Pulses',
        header: 'R2 pulse',
        size: 100,
        cell: ({ row }) => (
          <span className="tabular-nums">{dashIfEmpty(row.original.reseller2Pulses)}</span>
        ),
      },
      {
        accessorKey: 'userPulses',
        header: 'User pulse',
        size: 110,
        cell: ({ row }) => (
          <span className="tabular-nums">{dashIfEmpty(row.original.userPulses)}</span>
        ),
      },
      {
        accessorKey: 'smsCount',
        header: 'SMS',
        size: 90,
        cell: ({ row }) => (
          <span className="tabular-nums">{dashIfEmpty(row.original.smsCount)}</span>
        ),
      },
    ];
    const extras: ColumnDef<ResellerSummaryRow>[] = isReseller
      ? []
      : [
          {
            accessorKey: 'ttsCount',
            header: 'TTS variables',
            size: 130,
            cell: ({ row }) => (
              <span className="tabular-nums">{dashIfEmpty(row.original.ttsCount)}</span>
            ),
          },
        ];

    /* Resellers (parentType="reseller") don't show R1 columns.       *
     * Non-resellers (eg admins) see the full R1 + R2 + extras chain. */
    return isReseller ? [...r2, ...common] : [...r1, ...r2, ...common, ...extras];
  }, [isReseller]);

  if (!canView) return <Navigate to="/" replace />;

  return (
    <FillPage>
      <PageHeader
        title="Billing Summary"
        description="Campaign-level aggregated billing across resellers and end users."
      />

      <Card>
        <CardContent className="grid gap-3 p-4 md:grid-cols-4 md:items-end">
          <div className="space-y-1">
            <Label>User</Label>
            <Select
              value={username === '' ? ANY : username}
              onValueChange={(v) => setUsername(v === ANY ? '' : v)}
              disabled={subUsers.isLoading}
            >
              <SelectTrigger>
                <SelectValue placeholder={subUsers.isLoading ? 'Loading…' : 'All users'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>All users</SelectItem>
                {(subUsers.data ?? []).map((u) => (
                  <SelectItem key={u.username} value={u.username}>
                    {u.username}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>From</Label>
            <DatePicker value={startDate ?? null} onChange={setStartDate} placeholder="Any date" />
          </div>
          <div className="space-y-1">
            <Label>To</Label>
            <DatePicker value={endDate ?? null} onChange={setEndDate} placeholder="Any date" />
          </div>
          <Button
            onClick={() => runQuery()}
            disabled={summary.isPending}
            className="md:justify-self-end"
          >
            {summary.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Search className="h-4 w-4" />
            )}
            Search
          </Button>
        </CardContent>
      </Card>

      <DataTable<ResellerSummaryRow, unknown>
        tableId={`reports:reseller-summary:${isReseller ? 'reseller' : 'admin'}:v1`}
        columns={columns}
        data={rows}
        isLoading={summary.isPending}
        searchPlaceholder="Filter results…"
        emptyTitle="No campaigns"
        emptyDescription="No campaigns match the current filters."
        getRowId={(row, idx) => row.campaignId ?? `rs-${idx}`}
        exportFilename="reseller-summary.csv"
        pageSize={10}
      />
    </FillPage>
  );
}
